import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, ArrowRight, X } from "lucide-react";

/**
 * FloatingCTAs, single floating "Enquire now" action.
 * - Appears bottom-right after user scrolls past hero
 * - Compact icon-only chip on mobile so it never covers form fields / pay buttons
 * - Auto-hides on checkout, booking success, deposit, admin and any /book route
 * - User can dismiss for the session via the × chip
 */
export default function FloatingAvailability() {
 const { pathname } = useLocation();
 const navigate = useNavigate();
 const [scrolled, setScrolled] = useState(false);
 const [dismissed, setDismissed] = useState(() => {
  try { return sessionStorage.getItem("floating-ctas-dismissed") === "1"; } catch { return false; }
 });

 useEffect(() => {
  const onScroll = () => setScrolled(window.scrollY > 600);
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();
  return () => window.removeEventListener("scroll", onScroll);
 }, []);

 // Auto-hide on checkout, booking success, deposit, admin and any *book* route so the
 // button never overlaps Pay / Confirm buttons or admin table controls.
 const hiddenRoute = /\/book(\/|$)|\/booking\/success|\/deposit\/|\/admin(\/|$)|\/checkout|\/contact/i.test(pathname);
 if (hiddenRoute || !scrolled || dismissed) return null;

 const goToEnquiry = (e) => {
  e?.preventDefault?.();
  // Try to find an in-page enquiry anchor first
  for (const id of ["enquire", "enquiry", "final-cta", "contact"]) {
   const el = document.getElementById(id);
   if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
   }
  }
  // Fallback, route to the Contact enquiry form
  navigate("/contact#enquiry");
 };

 const dismiss = () => {
  try { sessionStorage.setItem("floating-ctas-dismissed", "1"); } catch { /* ignore */ }
  setDismissed(true);
 };

 return (
  <div
   className="fixed right-3 sm:right-5 lg:right-7 bottom-4 sm:bottom-20 lg:bottom-24 z-[60] flex flex-col items-end gap-2 sm:gap-3 print:hidden animate-in slide-in-from-bottom-3 duration-300"
   data-testid="floating-availability"
   style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
  >
   {/* Dismiss (always visible) */}
   <button
    type="button"
    onClick={dismiss}
    className="w-7 h-7 rounded-full bg-[#0A1E3D]/85 backdrop-blur text-white/85 ring-1 ring-white/25 shadow-lg flex items-center justify-center hover:bg-[#0A1E3D] active:scale-95 transition"
    aria-label="Hide enquiry button"
    data-testid="floating-dismiss"
   >
    <X className="w-3.5 h-3.5" strokeWidth={2.5} />
   </button>

   {/* ENQUIRE NOW, pink/fuchsia gradient */}
   <button
    type="button"
    onClick={goToEnquiry}
    className="group relative flex items-center gap-2 sm:gap-3 rounded-full pl-2 pr-2 sm:pl-3 sm:pr-5 py-2.5 sm:py-3 shadow-[0_20px_50px_-10px_rgba(217,70,239,0.5)] ring-1 ring-pink-200/50 hover:ring-pink-100 transition-all active:scale-95 hover:-translate-y-0.5 hover:scale-[1.02] overflow-hidden text-white"
    style={{ background: "linear-gradient(135deg, #EC4899 0%, #D946EF 50%, #8B5CF6 100%)" }}
    data-testid="floating-enquire-now"
    aria-label="Enquire about your event"
   >
    <span className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/30 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity" />
    <span className="relative flex items-center justify-center w-10 h-10 sm:w-9 sm:h-9 rounded-full bg-white/20 backdrop-blur ring-1 ring-white/40">
     <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
    </span>
    <div className="hidden sm:block relative text-left">
     <div className="text-[9px] uppercase tracking-[0.28em] font-black leading-none opacity-90">Fast reply</div>
     <div className="text-[14px] font-black tracking-tight leading-none mt-1">Enquire now</div>
    </div>
    <ArrowRight className="hidden sm:block relative w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.5} />
   </button>
  </div>
 );
}
